////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Home route — free-floating hero + staggered glass-tile content //
//           (1.0.3.md §4/§6)                                                //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  Recomposed wholesale for this phase: the hero is now HomeHero (free-
//  floating, no glass tile — §5's deliberate exception, mirrored by every
//  other page's <PageHero />), and every content section below it is a
//  staggered <GlassTile> via <Reveal>/<RevealGroup>
//  (components/content/Reveal.tsx) — the same entrance wiring
//  About/Contact/Careers/Legal/News/Projects use, rather than this page
//  hand-rolling its own whileInView/variants plumbing a second time. Copy/
//  content itself is UNCHANGED from 1.0.1/1.0.2 (still sourced via
//  lib/content.ts's section parsers) — this is a motion/presentation pass,
//  not a copywriting one (§8). Only genuinely new content is the stats
//  ticker and the news teaser section, neither of which existed on Home
//  before.
//
////////////////////////////////////////////////////////////////////////////////

import { Link } from 'react-router';
import { motion } from 'framer-motion';

import { usePageTitle } from '@/hooks/usePageTitle';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { getPage, listNews, splitSections, parseProjectTeasers, type ProjectTeaser, extractImages } from '@/lib/content';
import { cardHover, clipWipe, fadeRise, reduceHover, reduceVariants } from '@/lib/motion';
import { HomeHero, HERO_SCROLL_TARGET_ID } from '@/components/content/HomeHero';
import { GlassTile } from '@/components/content/GlassTile';
import { Reveal, RevealGroup } from '@/components/content/Reveal';
import { StatsTicker } from '@/components/content/StatsTicker';
import { NewsTeaserCard } from '@/components/content/NewsTeaserCard';
import { Markdown } from '@/components/content/Markdown';
import { ImageStrip } from '@/components/content/ImageStrip';
import { Img } from '@/components/media/Img';

/** Most-recent 3 articles, newest first — content/news.ts's `date` is an ISO
 * date string (`YYYY-MM-DD`), sortable lexicographically without a Date parse. */
function latestNews (count: number) {
  return [...listNews()].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, count);
}

/** §6: "News/project cards: clipWipe image reveals, hover grammar per §2" —
 * kept local rather than promoted to components/content/ alongside
 * NewsTeaserCard: it has no destination link yet (see the MARK below it), so
 * it is not yet a general-purpose card another page would reuse. */
function ProjectTeaserCard ({ teaser }: { teaser: ProjectTeaser }) {
  const reducedMotion = useReducedMotion();

  return (
    <motion.li
      className="overflow-hidden rounded-2xl border border-line"
      // `variants` (no own `initial`/`whileInView`) — inherits the wrapping
      // <RevealGroup>'s staggerGroup context, same convention as
      // NewsTeaserCard, so the card fades/rises on the group's beat.
      variants={reduceVariants(fadeRise, reducedMotion)}
      whileHover={reduceHover(cardHover, reducedMotion)}
    >
      {/* MARK — 1.0.3 post-review tweak #5 (root-cause fix): this image div used
          to carry its OWN `initial="hidden" whileInView="visible" viewport={...}`
          — a second, independent scroll observer keyed to the SAME "hidden"/
          "visible" variant names the parent <RevealGroup> (Home.tsx's caller)
          was ALSO propagating down through this card's own `motion.li` above.
          With `viewport={{ once: true }}` on both, whichever observer's callback
          fired first "consumed" that once-only trigger for this element while it
          was still held in the OTHER observer's "hidden" state — the left column
          (observed first, scrolled into view first) lost the race and its images
          never reached "visible". Removing the child's own observer entirely and
          leaving only `variants={clipWipe}` — no `initial`/`whileInView`/
          `viewport` of its own — makes it inherit the propagated state from the
          same <RevealGroup> ancestor instead, so there is exactly ONE observer
          per card grid, and every image reveals on the group's stagger beat.
          Same pattern <NewsTeaserCard /> below now follows. */}
      <motion.div
        className="overflow-hidden"
        variants={reduceVariants(clipWipe, reducedMotion)}
      >
        <Img src={teaser.image} alt={`${teaser.name} — ${teaser.suburb}`} className="h-48 w-full object-cover" />
      </motion.div>
      <div className="p-4">
        <p className="font-medium text-ink">{teaser.name}</p>
        <p className="text-sm text-muted">{teaser.suburb}</p>
        <p className="text-sm text-muted">{teaser.client}</p>
        <p className="mt-2 inline-block rounded-full bg-line px-3 py-1 text-xs text-ink">
          {teaser.status}
        </p>
      </div>
    </motion.li>
  );
}

export function Home () {
  usePageTitle('Home');

  const page = getPage('home');
  if (!page) return null; // Content pipeline guarantees this slug exists; see lib/content.ts.

  const sections = splitSections(page.bodyMd);
  const welcome = sections.find((s) => s.heading === 'Welcome');
  const projectsSection = sections.find((s) => s.heading === 'Discover our projects');
  const gallerySection = sections.find((s) => s.heading === 'Gallery');

  const teasers = projectsSection ? parseProjectTeasers(projectsSection.bodyMd) : [];
  const galleryImages = gallerySection ? extractImages(gallerySection.bodyMd) : [];
  const newsTeasers = latestNews(3);

  return (
    <div>
      <HomeHero />

      {welcome && (
        <section id={HERO_SCROLL_TARGET_ID} className="mx-auto max-w-6xl px-6 py-16" aria-labelledby="welcome-heading">
          <Reveal as="section">
            <GlassTile className="p-8 sm:p-12">
              <h2 id="welcome-heading" className="text-2xl font-semibold text-ink sm:text-4xl">Welcome</h2>
              <Markdown source={welcome.bodyMd} className="mt-6" />
            </GlassTile>
          </Reveal>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-6 py-16" aria-label="IRONSIDE by the numbers">
        <Reveal as="section">
          <GlassTile className="p-8 sm:p-12">
            <StatsTicker />
          </GlassTile>
        </Reveal>
      </section>

      {teasers.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16" id="projects" aria-labelledby="projects-heading">
          <Reveal as="section">
            <GlassTile className="p-8 sm:p-12">
              <h2 id="projects-heading" className="text-2xl font-semibold text-ink">Discover our projects</h2>
              {/* MARK — "View Project" carries no href in the scrape (no project detail page
                  existed at the time of the scrape). These are placeholder teasers pending real
                  data from GET /public/projects (1.0.2.md §5.2) — the strip links through to the
                  (currently empty) /projects listing rather than a specific, non-existent
                  detail page. Carried over unchanged from 1.0.1/1.0.2's Home. */}
              <p className="mt-2 max-w-2xl text-sm text-muted">
                Featured projects, carried over from the current site pending live project data
                from the API — placeholder until then.
              </p>
              <RevealGroup as="ul" className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {teasers.map((teaser) => (
                  <ProjectTeaserCard key={teaser.image} teaser={teaser} />
                ))}
              </RevealGroup>
              <Link to="/projects" className="mt-8 inline-block underline">
                View all projects
              </Link>
            </GlassTile>
          </Reveal>
        </section>
      )}

      {newsTeasers.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16" id="news" aria-labelledby="news-heading">
          <Reveal as="section">
            <GlassTile className="p-8 sm:p-12">
              <h2 id="news-heading" className="text-2xl font-semibold text-ink">Latest news</h2>
              <RevealGroup as="ul" className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
                {newsTeasers.map((article) => (
                  <NewsTeaserCard
                    key={article.slug}
                    slug={article.slug}
                    title={article.title}
                    publishedAt={article.date}
                    image={article.images[0] ?? null}
                  />
                ))}
              </RevealGroup>
              <Link to="/news" className="mt-8 inline-block underline">
                View all news
              </Link>
            </GlassTile>
          </Reveal>
        </section>
      )}

      {galleryImages.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16" id="gallery" aria-labelledby="gallery-heading">
          <Reveal as="section">
            <GlassTile className="p-8 sm:p-12">
              <h2 id="gallery-heading" className="text-2xl font-semibold text-ink">Gallery</h2>
              <div className="mt-6">
                <ImageStrip
                  images={galleryImages.map((image, i) => ({
                    src: image.src,
                    // MARK — no per-image alt text was captured for this gallery strip in the
                    // scrape (bare `![]()`); a generic but honest description is used pending a
                    // content-authoring pass with real captions. Carried over unchanged.
                    alt: `IRONSIDE project photography — gallery image ${i + 1}`,
                  }))}
                />
              </div>
            </GlassTile>
          </Reveal>
        </section>
      )}
    </div>
  );
}
